// The quick-add button (UX §1): bottom right on every screen — above the tab bar on a phone — with
// New lead, Walk-in, Task, Site visit and Note. The forms are fetched when first opened, so the button
// costs the first page almost nothing.
import { useNavigate } from '@tanstack/react-router';
import { cn, Menu, MenuContent, MenuItem, MenuTrigger } from '@tatra/ui';
import { CalendarCheck, ClipboardList, DoorOpen, NotebookPen, Plus, UserPlus } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { useAuth } from '../lib/auth.js';
import { quickAdd, type QuickAddKind, useQuickAdd } from '../lib/quick-add.js';

const QuickAddDialogs = lazy(() =>
  import('../features/leads/QuickAddDialogs.js').then((m) => ({ default: m.QuickAddDialogs })),
);

export function QuickAddButton() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { open } = useQuickAdd();
  const items: { key: QuickAddKind | 'walk-in'; label: string; icon: typeof Plus; allowed: boolean }[] = [
    { key: 'lead', label: 'New lead', icon: UserPlus, allowed: can('leads.lead.create') },
    { key: 'walk-in', label: 'Walk-in', icon: DoorOpen, allowed: can('leads.lead.create') },
    { key: 'task', label: 'Task', icon: ClipboardList, allowed: can('activity.task.manage') },
    { key: 'visit', label: 'Site visit', icon: CalendarCheck, allowed: can('activity.visit.manage') },
    { key: 'note', label: 'Note', icon: NotebookPen, allowed: can('leads.lead.update') },
  ];
  const allowed = items.filter((i) => i.allowed);
  if (allowed.length === 0) return null;

  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          <button
            type="button"
            aria-label="Quick add"
            data-testid="quick-add"
            className={cn(
              'fixed right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-fg shadow-[0_12px_30px_-10px_rgb(236_31_156/0.85)] transition-transform hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-95',
              // Above the phone tab bar (56 px + the safe area); bottom right on a desktop.
              'bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] lg:right-6 lg:bottom-6',
            )}
          >
            <Plus className="h-6 w-6" aria-hidden="true" />
          </button>
        </MenuTrigger>
        <MenuContent>
          {allowed.map((i) => (
            <MenuItem
              key={i.key}
              className="min-h-11 md:min-h-9"
              onSelect={() =>
                i.key === 'walk-in' ? void navigate({ to: '/walk-in' }) : quickAdd.open(i.key)
              }
            >
              <i.icon className="h-4 w-4" aria-hidden="true" /> {i.label}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
      {open && (
        <Suspense fallback={null}>
          <QuickAddDialogs />
        </Suspense>
      )}
    </>
  );
}
